import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Menu,
    Divider,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Speed as SpeedIcon,
    EmojiEvents as EmojiEventsIcon,
    MoreVert as MoreVertIcon,
    LockReset as LockResetIcon,
    ToggleOn as ToggleOnIcon,
    Pin as PinIcon,
    SendToMobile as SendToMobileIcon,
    RestartAlt as RestartAltIcon,
    History as HistoryIcon,
    FamilyRestroom as FamilyRestroomIcon,
    FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import { PinRevealCell } from '../../components/admin/PinRevealCell';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/cs';
import {
    useUserByIdQuery,
    useUpdateUserMutation,
    useResetUserPasswordMutation,
    useToggleUserStatusMutation,
    useSetUserPinMutation,
    useDeleteUserPinMutation,
    useDeleteUserMutation,
    useSendUserTestNotificationMutation,
    useResetUserTestLimitMutation,
    useUserTestHistoryQuery,
} from '../../api/JsdhNotifikaceService/service/AdminMutations';
import {
    useKontrolyForUserQuery,
    useKontrolyHistorieQuery,
    useCreateKontrolaMutation,
    useUpdateKontrolaMutation,
    useDeleteKontrolaMutation,
} from '../../api/JsdhNotifikaceService/service/KontrolyMutations';
import { useDownloadPosudekMutation } from '../../api/JsdhNotifikaceService/service/KontrolyDokumentMutations';
import { downloadFile } from '../../utils/downloadFile';
import { useStatistikaUzivateleQuery } from '../../api/JsdhNotifikaceService/service/StatistikaMutations';
import { useUzivatelAktivitaQuery } from '../../api/JsdhNotifikaceService/service/DochazkaMutations';
import { useAllSpecializaceQuery } from '../../api/JsdhNotifikaceService/service/SpecializaceMutations';
import { useActiveTechnikaQuery } from '../../api/JsdhNotifikaceService/service/TechnikaMutations';
import { formatujDobuReakce } from '../../utils/statistikyUtils';
import RodinnyPristupDialog from '../../components/RodinnyPristupDialog';
import type { KontrolaStatusResponse, UzivatelKontrola, CreateKontrolaRequest } from '../../api/JsdhNotifikaceService/generated/models/Kontrola';
import type { MetadataFieldSchema } from '../../api/JsdhNotifikaceService/generated/models/TypKontroly';
import type { UpdateUserRequest } from '../../api/JsdhNotifikaceService/generated/models/UpdateUserRequest';
import VystrojniKartaSection from '../../components/inventar/VystrojniKartaSection';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import PrevzatoPopisek from '../../components/admin/kontroly/PrevzatoPopisek';

dayjs.locale('cs');

const FUNKCE_OPTIONS = [
    { kod: 'VD', nazev: 'Velitel družstva' },
    { kod: 'S', nazev: 'Strojník' },
    { kod: 'H', nazev: 'Hasič' },
];

// ==========================================
// HELPER: Status Chip
// ==========================================

const StatusChip: React.FC<{ status: KontrolaStatusResponse }> = ({ status }) => {
    if (!status.relevantni) {
        return <Typography variant="body2" color="text.disabled">—</Typography>;
    }

    switch (status.status) {
        case 'PLATNA':
            return (
                <Chip
                    color="success"
                    label={`Platná do ${dayjs(status.datumExpirace).format('DD.MM.YYYY')}`}
                    size="small"
                />
            );
        case 'BLIZI_SE_EXPIRACE':
            return (
                <Chip
                    color="warning"
                    label={`Vyprší ${dayjs(status.datumExpirace).format('DD.MM.YYYY')}`}
                    size="small"
                />
            );
        case 'EXPIROVANA':
            return (
                <Chip
                    color="error"
                    label={`Expirovaná ${dayjs(status.datumExpirace).format('DD.MM.YYYY')}`}
                    size="small"
                />
            );
        case 'CHYBI':
            return <Chip variant="outlined" label="Chybí" size="small" />;
        default:
            return null;
    }
};

// ==========================================
// HELPER: Metadata Form Fields
// ==========================================

const MetadataFields: React.FC<{
    schema: MetadataFieldSchema[];
    values: Record<string, any>;
    onChange: (values: Record<string, any>) => void;
}> = ({ schema, values, onChange }) => {
    const { data: technika } = useActiveTechnikaQuery();

    return (
        <>
            {schema.map((field) => {
                if (field.type === 'technika') {
                    return (
                        <FormControl key={field.key} fullWidth margin="dense" size="small">
                            <InputLabel>{field.label}{field.required ? ' *' : ''}</InputLabel>
                            <Select
                                value={values[field.key] ?? ''}
                                label={`${field.label}${field.required ? ' *' : ''}`}
                                onChange={(e) => onChange({ ...values, [field.key]: e.target.value })}
                            >
                                <MenuItem value="">— Nevybráno —</MenuItem>
                                {technika?.map((t) => (
                                    <MenuItem key={t.id} value={t.id}>
                                        {t.volaciZnak} - {t.nazev}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                }

                if (field.type === 'select' && field.options) {
                    return (
                        <FormControl key={field.key} fullWidth margin="dense" size="small">
                            <InputLabel>{field.label}{field.required ? ' *' : ''}</InputLabel>
                            <Select
                                value={values[field.key] ?? ''}
                                label={`${field.label}${field.required ? ' *' : ''}`}
                                onChange={(e) => onChange({ ...values, [field.key]: e.target.value })}
                            >
                                <MenuItem value="">— Nevybráno —</MenuItem>
                                {field.options.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                }

                return (
                    <TextField
                        key={field.key}
                        label={field.label}
                        type={field.type === 'number' ? 'number' : 'text'}
                        value={values[field.key] ?? ''}
                        onChange={(e) => onChange({
                            ...values,
                            [field.key]: field.type === 'number'
                                ? (e.target.value === '' ? '' : Number(e.target.value))
                                : e.target.value
                        })}
                        required={field.required}
                        fullWidth
                        margin="dense"
                        size="small"
                    />
                );
            })}
        </>
    );
};

// ==========================================
// DIALOG: Zaznamenat kontrolu
// ==========================================

const KontrolaDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    uzivatelId: number;
    uzivatelJmeno: string;
    kontrolaStatus: KontrolaStatusResponse;
}> = ({ open, onClose, uzivatelId, uzivatelJmeno, kontrolaStatus }) => {
    const [datumSplneni, setDatumSplneni] = useState<Dayjs | null>(dayjs());
    const [poznamka, setPoznamka] = useState('');
    const [metadataValues, setMetadataValues] = useState<Record<string, any>>({});
    const [editingId, setEditingId] = useState<number | null>(null);

    const createMutation = useCreateKontrolaMutation();
    const updateMutation = useUpdateKontrolaMutation();
    const deleteMutation = useDeleteKontrolaMutation();
    const { data: historie } = useKontrolyHistorieQuery(
        open ? uzivatelId : null,
        open ? kontrolaStatus.typKontrolyId : null
    );

    const metadataSchema: MetadataFieldSchema[] = useMemo(() => {
        if (!kontrolaStatus.metadataSchema) return [];
        try {
            return JSON.parse(kontrolaStatus.metadataSchema);
        } catch {
            return [];
        }
    }, [kontrolaStatus.metadataSchema]);

    const handleReset = () => {
        setDatumSplneni(dayjs());
        setPoznamka('');
        setMetadataValues({});
        setEditingId(null);
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const handleEdit = (record: UzivatelKontrola) => {
        setDatumSplneni(dayjs(record.datumSplneni));
        setPoznamka(record.poznamka || '');
        setEditingId(record.id);
        if (record.metadata) {
            try {
                setMetadataValues(JSON.parse(record.metadata));
            } catch {
                setMetadataValues({});
            }
        } else {
            setMetadataValues({});
        }
    };

    const handleSave = () => {
        if (!datumSplneni) return;

        const metadataJson = metadataSchema.length > 0 && Object.keys(metadataValues).some(k => metadataValues[k] !== '' && metadataValues[k] !== undefined)
            ? JSON.stringify(metadataValues)
            : undefined;

        const request: CreateKontrolaRequest = {
            uzivatelId,
            typKontrolyId: kontrolaStatus.typKontrolyId,
            datumSplneni: datumSplneni.format('YYYY-MM-DD'),
            poznamka: poznamka || undefined,
            metadata: metadataJson,
        };

        if (editingId) {
            updateMutation.mutate({ id: editingId, data: request }, {
                onSuccess: () => handleReset(),
            });
        } else {
            createMutation.mutate(request, {
                onSuccess: () => handleReset(),
            });
        }
    };

    const handleDelete = (record: UzivatelKontrola) => {
        if (window.confirm('Opravdu smazat tento záznam?')) {
            deleteMutation.mutate({ id: record.id, uzivatelId });
        }
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {kontrolaStatus.typKontrolyNazev} — {uzivatelJmeno}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <DatePicker
                        label="Datum splnění"
                        value={datumSplneni}
                        onChange={setDatumSplneni}
                        format="DD.MM.YYYY"
                        slotProps={{ textField: { fullWidth: true, size: 'small', margin: 'dense' } }}
                    />

                    <TextField
                        label="Poznámka"
                        value={poznamka}
                        onChange={(e) => setPoznamka(e.target.value)}
                        fullWidth
                        margin="dense"
                        size="small"
                        multiline
                        rows={2}
                    />

                    {metadataSchema.length > 0 && (
                        <MetadataFields
                            schema={metadataSchema}
                            values={metadataValues}
                            onChange={setMetadataValues}
                        />
                    )}
                </Box>

                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!datumSplneni || isSaving}
                        size="small"
                    >
                        {isSaving ? <CircularProgress size={20} /> : editingId ? 'Uložit změny' : 'Zaznamenat'}
                    </Button>
                    {editingId && (
                        <Button variant="outlined" onClick={handleReset} size="small">
                            Zrušit editaci
                        </Button>
                    )}
                </Box>

                {historie && historie.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>Historie</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Datum splnění</TableCell>
                                        <TableCell>Expirace</TableCell>
                                        <TableCell>Poznámka</TableCell>
                                        <TableCell align="right">Akce</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {historie.map((record) => (
                                        <TableRow key={record.id}>
                                            <TableCell>{dayjs(record.datumSplneni).format('DD.MM.YYYY')}</TableCell>
                                            <TableCell>{dayjs(record.datumExpirace).format('DD.MM.YYYY')}</TableCell>
                                            <TableCell>{record.poznamka || '—'}</TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Upravit">
                                                    <IconButton size="small" onClick={() => handleEdit(record)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Smazat">
                                                    <IconButton size="small" onClick={() => handleDelete(record)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Zavřít</Button>
            </DialogActions>
        </Dialog>
    );
};

// ==========================================
// DIALOG: Editace uživatele
// ==========================================

const UserEditDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    user: { id: number; jmeno: string; telefon: number; email?: string; funkce?: any; specializace?: any; aktivni: boolean; notifikaceEnabled?: boolean; hzsSmena?: string | null; hzsZtlumitNotifikace?: boolean; datumNarozeni?: string | null };
}> = ({ open, onClose, user }) => {
    const { data: allSpecializace } = useAllSpecializaceQuery();
    const updateMutation = useUpdateUserMutation();

    const getFunkceKod = (f: unknown): string | undefined => {
        if (typeof f === 'string') return f;
        if (f && typeof f === 'object' && 'kod' in f && typeof (f as { kod: unknown }).kod === 'string') {
            return (f as { kod: string }).kod;
        }
        return undefined;
    };
    const getSpecializaceKod = (s: unknown): string | undefined => {
        if (typeof s === 'string') return s;
        if (s && typeof s === 'object' && 'kod' in s && typeof (s as { kod: unknown }).kod === 'string') {
            return (s as { kod: string }).kod;
        }
        return undefined;
    };

    const initFunkce = Array.isArray(user.funkce)
        ? user.funkce.map(getFunkceKod).filter((k): k is string => k !== undefined)
        : user.funkce ? [getFunkceKod(user.funkce)].filter((k): k is string => k !== undefined) : [];
    const initSpecializace = Array.isArray(user.specializace)
        ? user.specializace.map(getSpecializaceKod).filter((k): k is string => k !== undefined)
        : user.specializace ? [getSpecializaceKod(user.specializace)].filter((k): k is string => k !== undefined) : [];

    const [formData, setFormData] = useState({
        jmeno: user.jmeno,
        telefon: user.telefon,
        email: user.email || '',
        funkce: initFunkce,
        specializace: initSpecializace,
        aktivni: user.aktivni,
        notifikaceEnabled: user.notifikaceEnabled ?? true,
        hzsSmena: user.hzsSmena || '',
        hzsZtlumitNotifikace: user.hzsZtlumitNotifikace ?? false,
        datumNarozeni: user.datumNarozeni || null as string | null,
    });

    // Reset form when user changes
    React.useEffect(() => {
        const funkce = Array.isArray(user.funkce)
            ? user.funkce.map(getFunkceKod).filter((k): k is string => k !== undefined)
            : user.funkce ? [getFunkceKod(user.funkce)].filter((k): k is string => k !== undefined) : [];
        const specializace = Array.isArray(user.specializace)
            ? user.specializace.map(getSpecializaceKod).filter((k): k is string => k !== undefined)
            : user.specializace ? [getSpecializaceKod(user.specializace)].filter((k): k is string => k !== undefined) : [];
        setFormData({
            jmeno: user.jmeno,
            telefon: user.telefon,
            email: user.email || '',
            funkce,
            specializace,
            aktivni: user.aktivni,
            notifikaceEnabled: user.notifikaceEnabled ?? true,
            hzsSmena: user.hzsSmena || '',
            hzsZtlumitNotifikace: user.hzsZtlumitNotifikace ?? false,
            datumNarozeni: user.datumNarozeni || null,
        });
    }, [user]);

    const handleSubmit = () => {
        const updateData: UpdateUserRequest = {
            jmeno: formData.jmeno,
            telefon: formData.telefon,
            email: formData.email || undefined,
            funkce: formData.funkce,
            specializace: formData.specializace,
            aktivni: formData.aktivni,
            notifikaceEnabled: formData.notifikaceEnabled,
            hzsSmena: formData.hzsSmena || null,
            hzsZtlumitNotifikace: formData.hzsZtlumitNotifikace,
            datumNarozeni: formData.datumNarozeni || null,
        };
        updateMutation.mutate(
            { id: user.id, data: updateData },
            { onSuccess: onClose }
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Upravit uživatele</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label="Jméno"
                        fullWidth
                        required
                        value={formData.jmeno}
                        onChange={(e) => setFormData({ ...formData, jmeno: e.target.value })}
                    />
                    <TextField
                        label="Telefon"
                        type="tel"
                        fullWidth
                        required
                        value={formData.telefon || ''}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setFormData({ ...formData, telefon: parseInt(value) || 0 });
                        }}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        InputLabelProps={{ shrink: !!formData.telefon }}
                        autoComplete="off"
                    />
                    <TextField
                        label="Email"
                        type="email"
                        fullWidth
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                    <FormControl fullWidth>
                        <InputLabel>Funkce</InputLabel>
                        <Select
                            multiple
                            value={formData.funkce}
                            label="Funkce"
                            onChange={(e) => setFormData({ ...formData, funkce: e.target.value as string[] })}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(selected as string[]).map((value) => {
                                        const option = FUNKCE_OPTIONS.find(o => o.kod === value);
                                        return <Chip key={value} label={option?.nazev || value} size="small" />;
                                    })}
                                </Box>
                            )}
                        >
                            {FUNKCE_OPTIONS.map((option) => (
                                <MenuItem key={option.kod} value={option.kod}>
                                    {option.nazev} ({option.kod})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Specializace</InputLabel>
                        <Select
                            multiple
                            value={formData.specializace}
                            label="Specializace"
                            onChange={(e) => setFormData({ ...formData, specializace: e.target.value as string[] })}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(selected as string[]).map((value) => {
                                        const spec = allSpecializace?.find(s => s.kod === value);
                                        return <Chip key={value} label={spec?.nazev || value} size="small" color="secondary" />;
                                    })}
                                </Box>
                            )}
                        >
                            {(allSpecializace || []).map((spec) => (
                                <MenuItem key={spec.kod} value={spec.kod}>
                                    {spec.nazev} ({spec.kod})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.aktivni}
                                    onChange={(e) => setFormData({ ...formData, aktivni: e.target.checked })}
                                />
                            }
                            label="Aktivní"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.notifikaceEnabled}
                                    onChange={(e) => setFormData({ ...formData, notifikaceEnabled: e.target.checked })}
                                />
                            }
                            label="Notifikace povoleny"
                        />
                    </Box>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>HZS Směna</InputLabel>
                        <Select
                            value={formData.hzsSmena || ''}
                            onChange={(e) => setFormData({ ...formData, hzsSmena: e.target.value as string })}
                            label="HZS Směna"
                        >
                            <MenuItem value="">Žádná (není HZS)</MenuItem>
                            <MenuItem value="A">Směna A</MenuItem>
                            <MenuItem value="B">Směna B</MenuItem>
                            <MenuItem value="C">Směna C</MenuItem>
                        </Select>
                    </FormControl>
                    {formData.hzsSmena && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.hzsZtlumitNotifikace}
                                    onChange={(e) => setFormData({ ...formData, hzsZtlumitNotifikace: e.target.checked })}
                                />
                            }
                            label="Ztlumit notifikace během HZS směny"
                        />
                    )}
                    <DatePicker
                        label="Datum narození"
                        value={formData.datumNarozeni ? dayjs(formData.datumNarozeni) : null}
                        onChange={(val) => setFormData({ ...formData, datumNarozeni: val ? val.format('YYYY-MM-DD') : null })}
                        format="DD.MM.YYYY"
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                size: 'small',
                                helperText: 'Potřebné pro šifrování e-mailu s posudkem',
                            },
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Zrušit</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!formData.jmeno || !formData.telefon || updateMutation.isPending}
                >
                    {updateMutation.isPending ? <CircularProgress size={20} /> : 'Uložit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ==========================================
// MAIN: Detail User Page
// ==========================================

const DetailUserPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const userId = id ? parseInt(id, 10) : null;

    const { data: user, isLoading, error } = useUserByIdQuery(userId!);
    const { data: kontroly } = useKontrolyForUserQuery(userId);
    const { data: statistiky } = useStatistikaUzivateleQuery(userId);
    const { data: aktivita } = useUzivatelAktivitaQuery(userId);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [kontrolaDialogState, setKontrolaDialogState] = useState<{
        open: boolean;
        kontrolaStatus: KontrolaStatusResponse;
    } | null>(null);

    // Action menu state
    const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [sendResetEmail, setSendResetEmail] = useState(true);
    const [pinDialogOpen, setPinDialogOpen] = useState(false);
    const [newPin, setNewPin] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [testNotifDialogOpen, setTestNotifDialogOpen] = useState(false);
    const [testNotifOptions, setTestNotifOptions] = useState({ fcm: true });
    const [resetLimitDialogOpen, setResetLimitDialogOpen] = useState(false);
    const [testHistoryDialogOpen, setTestHistoryDialogOpen] = useState(false);
    const [rodinnyPristupOpen, setRodinnyPristupOpen] = useState(false);

    // Action mutations
    const resetPasswordMutation = useResetUserPasswordMutation();
    const toggleStatusMutation = useToggleUserStatusMutation();
    const setUserPinMutation = useSetUserPinMutation();
    const deleteUserPinMutation = useDeleteUserPinMutation();
    const deleteUserMutation = useDeleteUserMutation();
    const testNotificationMutation = useSendUserTestNotificationMutation();
    const resetTestLimitMutation = useResetUserTestLimitMutation();
    const { data: testHistory, isLoading: testHistoryLoading } = useUserTestHistoryQuery(testHistoryDialogOpen ? userId : null);
    const downloadPosudekMutation = useDownloadPosudekMutation();
    // HO-84 gating UI: stažení posudku pod MODUL_EMAIL (sjednoceno s e-mailovou částí HO-22),
    // výstrojní karta pod MODUL_INVENTAR (inventářová doména). Fail-closed (skryté, když flag OFF).
    const { enabled: emailFlagEnabled } = useFeatureFlag('MODUL_EMAIL');
    const { enabled: inventarFlagEnabled } = useFeatureFlag('MODUL_INVENTAR');

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Chyba při načítání uživatele: {error.message}</Alert>;
    }

    if (!user) {
        return <Alert severity="warning">Uživatel nenalezen.</Alert>;
    }

    const getFunkceLabel = (f: unknown): string | undefined => {
        if (typeof f === 'string') return f;
        if (f && typeof f === 'object' && 'kod' in f) return String((f as { kod: unknown }).kod);
        return undefined;
    };
    const getSpecializaceLabel = (s: unknown): string | undefined => {
        if (typeof s === 'string') return s;
        if (s && typeof s === 'object' && 'kod' in s) return String((s as { kod: unknown }).kod);
        return undefined;
    };

    const funkceArray = Array.isArray(user.funkce) ? user.funkce : user.funkce ? [user.funkce] : [];
    const specializaceArray = Array.isArray(user.specializace) ? user.specializace : user.specializace ? [user.specializace] : [];

    const relevantniKontroly = kontroly?.filter(k => k.relevantni) || [];

    return (
        <Box>
            {/* Header */}
            <PageHeader
                title={user.jmeno}
                icon={
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBackIcon />
                    </IconButton>
                }
                action={
                    <>
                        <Chip
                            label={user.aktivni ? 'Aktivní' : 'Neaktivní'}
                            color={user.aktivni ? 'success' : 'default'}
                        />
                        <IconButton onClick={(e) => setActionMenuAnchor(e.currentTarget)}>
                            <MoreVertIcon />
                        </IconButton>
                    </>
                }
            />

            {/* Content grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, sm: 3 }, mb: 3 }}>
                {/* Sekce 1: Základní informace */}
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography variant="h6" gutterBottom>Základní informace</Typography>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: { xs: 120, sm: 180 } }}>Jméno</TableCell>
                                <TableCell>{user.jmeno}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Telefon</TableCell>
                                <TableCell>{user.telefon || '–'}</TableCell>
                            </TableRow>
                            {user.email && (
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Funkce</TableCell>
                                <TableCell>
                                    {funkceArray.length > 0 ? (
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {funkceArray.map((f, i) => {
                                                const kod = getFunkceLabel(f);
                                                const option = FUNKCE_OPTIONS.find(o => o.kod === kod);
                                                return <Chip key={kod || i} label={option?.nazev || kod || '?'} size="small" variant="outlined" />;
                                            })}
                                        </Box>
                                    ) : '–'}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Specializace</TableCell>
                                <TableCell>
                                    {specializaceArray.length > 0 ? (
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {specializaceArray.map((s, i) => {
                                                const kod = getSpecializaceLabel(s);
                                                return <Chip key={kod || i} label={kod || '?'} size="small" variant="outlined" color="secondary" />;
                                            })}
                                        </Box>
                                    ) : '–'}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Aktivní</TableCell>
                                <TableCell>
                                    <Chip
                                        label={user.aktivni ? 'Ano' : 'Ne'}
                                        color={user.aktivni ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Notifikace</TableCell>
                                <TableCell>
                                    <Chip
                                        label={user.notifikaceEnabled !== false ? 'Povoleny' : 'Zakázány'}
                                        color={user.notifikaceEnabled !== false ? 'primary' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>PIN</TableCell>
                                <TableCell><PinRevealCell userId={user.id} hasPin={!!user.hasPin} /></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>HZS Směna</TableCell>
                                <TableCell>
                                    {user.hzsSmena ? (
                                        <Chip
                                            label={`Směna ${user.hzsSmena}`}
                                            color="info"
                                            size="small"
                                        />
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">–</Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Datum narození</TableCell>
                                <TableCell>
                                    {user.datumNarozeni
                                        ? dayjs(user.datumNarozeni).format('DD.MM.YYYY')
                                        : <Typography variant="body2" color="warning.main">Nevyplněno</Typography>
                                    }
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Paper>

                {/* Sekce 3: Statistiky odpovědí */}
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography variant="h6" gutterBottom>Statistiky odpovědí</Typography>
                    {statistiky ? (
                        <>
                            {/* Hlavní karty */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                                <Card variant="outlined" sx={{ textAlign: 'center' }}>
                                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                        <Typography variant="h4">{statistiky.celkem.celkemOdpovedi}</Typography>
                                        <Typography variant="body2" color="text.secondary">Celkem odpovědí</Typography>
                                    </CardContent>
                                </Card>
                                <Card variant="outlined" sx={{ textAlign: 'center' }}>
                                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                        <CheckCircleIcon color="success" sx={{ fontSize: 24 }} />
                                        <Typography variant="h4">{statistiky.celkem.pocetJedu}</Typography>
                                        <Typography variant="body2" color="text.secondary">JEDU</Typography>
                                    </CardContent>
                                </Card>
                                <Card variant="outlined" sx={{ textAlign: 'center' }}>
                                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                        <CancelIcon color="error" sx={{ fontSize: 24 }} />
                                        <Typography variant="h4">{statistiky.celkem.pocetNejedu}</Typography>
                                        <Typography variant="body2" color="text.secondary">NEJEDU</Typography>
                                    </CardContent>
                                </Card>
                                <Card variant="outlined" sx={{ textAlign: 'center' }}>
                                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                        <SpeedIcon color="info" sx={{ fontSize: 24 }} />
                                        <Typography variant="h4">{formatujDobuReakce(statistiky.prumernaDobaReakce)}</Typography>
                                        <Typography variant="body2" color="text.secondary">Prům. reakce</Typography>
                                    </CardContent>
                                </Card>
                            </Box>

                            {/* Období srovnání */}
                            <Typography variant="subtitle2" gutterBottom>Srovnání podle období</Typography>
                            <Table size="small" sx={{ mb: 2 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Období</TableCell>
                                        <TableCell align="center">Odpovědi</TableCell>
                                        <TableCell align="center">JEDU</TableCell>
                                        <TableCell align="center">Účast</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[
                                        { label: 'Dnes', data: statistiky.dnes },
                                        { label: 'Týden', data: statistiky.tyden },
                                        { label: 'Měsíc', data: statistiky.mesic },
                                        { label: 'Celkem', data: statistiky.celkem },
                                    ].map(({ label, data }) => (
                                        <TableRow key={label}>
                                            <TableCell>{label}</TableCell>
                                            <TableCell align="center">{data.celkemOdpovedi} / {data.celkemPoplachu}</TableCell>
                                            <TableCell align="center">{data.pocetJedu}</TableCell>
                                            <TableCell align="center">
                                                {data.procentoUcasti !== null ? `${Math.round(data.procentoUcasti)} %` : '–'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            {/* Pořadí v jednotce */}
                            {(statistiky.poradiRychlost || statistiky.poradiAktivita) && (
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {statistiky.poradiRychlost && (
                                        <Chip
                                            icon={<EmojiEventsIcon />}
                                            label={`Rychlost: ${statistiky.poradiRychlost}. / ${statistiky.pocetClenuJednotky}`}
                                            variant="outlined"
                                            color={statistiky.poradiRychlost <= 3 ? 'success' : 'default'}
                                        />
                                    )}
                                    {statistiky.poradiAktivita && (
                                        <Chip
                                            icon={<EmojiEventsIcon />}
                                            label={`Aktivita: ${statistiky.poradiAktivita}. / ${statistiky.pocetClenuJednotky}`}
                                            variant="outlined"
                                            color={statistiky.poradiAktivita <= 3 ? 'success' : 'default'}
                                        />
                                    )}
                                </Box>
                            )}
                        </>
                    ) : (
                        <Typography color="text.secondary">Načítání statistik...</Typography>
                    )}
                </Paper>
            </Box>

            {/* Sekce 2: Kontroly a povinnosti */}
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom>Kontroly a povinnosti</Typography>
                {relevantniKontroly.length > 0 ? (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Typ kontroly</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Poslední splnění</TableCell>
                                    <TableCell>Expirace</TableCell>
                                    <TableCell align="right">Akce</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {relevantniKontroly.map((kontrola) => (
                                    <TableRow key={kontrola.typKontrolyId}>
                                        <TableCell>
                                            {kontrola.typKontrolyNazev}
                                            <PrevzatoPopisek kontrola={kontrola} />
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={kontrola} />
                                        </TableCell>
                                        <TableCell>
                                            {kontrola.datumSplneni
                                                ? dayjs(kontrola.datumSplneni).format('DD.MM.YYYY')
                                                : '–'}
                                        </TableCell>
                                        <TableCell>
                                            {kontrola.datumExpirace
                                                ? dayjs(kontrola.datumExpirace).format('DD.MM.YYYY')
                                                : '–'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setKontrolaDialogState({
                                                    open: true,
                                                    kontrolaStatus: kontrola,
                                                })}
                                            >
                                                Zaznamenat / Historie
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography color="text.secondary">
                        {kontroly ? 'Žádné relevantní kontroly pro tohoto uživatele.' : 'Načítání kontrol...'}
                    </Typography>
                )}
            </Paper>

            {/* Sekce 3: Výstrojní karta (HO-84: gated MODUL_INVENTAR) */}
            {userId && inventarFlagEnabled && <VystrojniKartaSection userId={userId} />}

            {/* Sekce: Účast na akcích */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mt: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom>Účast na akcích {aktivita ? `(${aktivita.pocet})` : ''}</Typography>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Akce</TableCell>
                                <TableCell>Typ</TableCell>
                                <TableCell>Datum</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {aktivita?.akce.map((a) => (
                                <TableRow key={a.akceId}>
                                    <TableCell>{a.nazev}</TableCell>
                                    <TableCell>{a.typ}</TableCell>
                                    <TableCell>{new Date(a.datum).toLocaleDateString('cs')}</TableCell>
                                </TableRow>
                            ))}
                            {aktivita && aktivita.pocet === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3}>Žádná uzavřená účast</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Dialogy */}
            {editDialogOpen && (
                <UserEditDialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    user={user}
                />
            )}

            {kontrolaDialogState && (
                <KontrolaDialog
                    open={kontrolaDialogState.open}
                    onClose={() => setKontrolaDialogState(null)}
                    uzivatelId={userId!}
                    uzivatelJmeno={user.jmeno}
                    kontrolaStatus={kontrolaDialogState.kontrolaStatus}
                />
            )}

            <RodinnyPristupDialog
                open={rodinnyPristupOpen}
                onClose={() => setRodinnyPristupOpen(false)}
                userId={userId!}
                userJmeno={user.jmeno}
            />

            {/* Action Menu */}
            <Menu
                anchorEl={actionMenuAnchor}
                open={Boolean(actionMenuAnchor)}
                onClose={() => setActionMenuAnchor(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={() => { setActionMenuAnchor(null); setEditDialogOpen(true); }}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Upravit</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); toggleStatusMutation.mutate({ id: user.id, aktivni: !user.aktivni }); }}>
                    <ListItemIcon><ToggleOnIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{user.aktivni ? 'Deaktivovat' : 'Aktivovat'}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); setPasswordDialogOpen(true); }}>
                    <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Reset hesla</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); setPinDialogOpen(true); }}>
                    <ListItemIcon><PinIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Nastavit PIN</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); setRodinnyPristupOpen(true); }}>
                    <ListItemIcon><FamilyRestroomIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Rodinný přístup</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => { setActionMenuAnchor(null); setTestNotifDialogOpen(true); }}
                    disabled={!user.aktivni}
                >
                    <ListItemIcon><SendToMobileIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Test notifikace</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); setResetLimitDialogOpen(true); }}>
                    <ListItemIcon><RestartAltIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Reset limitu testů</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); setTestHistoryDialogOpen(true); }}>
                    <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Historie testů</ListItemText>
                </MenuItem>
                {emailFlagEnabled && (
                    <MenuItem
                        onClick={() => {
                            setActionMenuAnchor(null);
                            downloadPosudekMutation.mutate(user.id, {
                                onSuccess: ({ blob, filename }) => downloadFile(blob, filename),
                            });
                        }}
                        disabled={downloadPosudekMutation.isPending}
                    >
                        <ListItemIcon>
                            {downloadPosudekMutation.isPending
                                ? <CircularProgress size={18} />
                                : <FileDownloadIcon fontSize="small" />
                            }
                        </ListItemIcon>
                        <ListItemText>Stáhnout posudek</ListItemText>
                    </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={() => { setActionMenuAnchor(null); setDeleteDialogOpen(true); }} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Smazat</ListItemText>
                </MenuItem>
            </Menu>

            {/* Reset Password Dialog */}
            <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Reset hesla — {user.jmeno}</DialogTitle>
                <DialogContent>
                    <FormControlLabel
                        control={<Switch checked={sendResetEmail} onChange={(e) => setSendResetEmail(e.target.checked)} />}
                        label="Odeslat odkaz na reset hesla emailem"
                        sx={{ mt: 1, mb: 2 }}
                    />
                    {!sendResetEmail && (
                        <TextField
                            label="Nové heslo"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            fullWidth
                            type="password"
                        />
                    )}
                    {sendResetEmail && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Na email uživatele bude odeslán odkaz pro nastavení nového hesla.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPasswordDialogOpen(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            resetPasswordMutation.mutate(
                                { id: user.id, newPassword: sendResetEmail ? undefined : newPassword, sendPasswordEmail: sendResetEmail },
                                { onSuccess: () => { setPasswordDialogOpen(false); setNewPassword(''); } }
                            );
                        }}
                        disabled={resetPasswordMutation.isPending || (!sendResetEmail && !newPassword)}
                    >
                        {sendResetEmail ? 'Odeslat email' : 'Resetovat heslo'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* PIN Dialog */}
            <Dialog open={pinDialogOpen} onClose={() => setPinDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Nastavit PIN — {user.jmeno}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="PIN"
                        value={newPin}
                        onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        fullWidth
                        sx={{ mt: 1 }}
                        helperText={newPin.length > 0 && newPin.length < 4 ? 'PIN musí mít 4-6 číslic' : '4-6 číslic'}
                        error={newPin.length > 0 && newPin.length < 4}
                    />
                </DialogContent>
                <DialogActions>
                    {user.hasPin && (
                        <Button
                            color="error"
                            onClick={() => {
                                deleteUserPinMutation.mutate(user.id, { onSuccess: () => { setPinDialogOpen(false); setNewPin(''); } });
                            }}
                            disabled={deleteUserPinMutation.isPending}
                        >
                            Smazat PIN
                        </Button>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={() => setPinDialogOpen(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setUserPinMutation.mutate(
                                { id: user.id, pin: newPin },
                                { onSuccess: () => { setPinDialogOpen(false); setNewPin(''); } }
                            );
                        }}
                        disabled={setUserPinMutation.isPending || !/^\d{4,6}$/.test(newPin)}
                    >
                        Nastavit PIN
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Test Notification Dialog */}
            <Dialog open={testNotifDialogOpen} onClose={() => setTestNotifDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Test notifikace — {user.jmeno}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Odešle běžnou testovací notifikaci, ne o kritický poplach.
                    </Typography>
                    <FormControlLabel
                        control={<Switch checked={testNotifOptions.fcm} onChange={(e) => setTestNotifOptions(prev => ({ ...prev, fcm: e.target.checked }))} />}
                        label={`FCM${(user.validFcmTokenCount ?? 0) > 0 ? ` (${user.validFcmTokenCount} zařízení)` : ''}`}
                        disabled={!user.validFcmTokenCount}
                    />
                    {!testNotifOptions.fcm && (
                        <Alert severity="warning" sx={{ mt: 1 }}>Vyberte alespoň jeden kanál.</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestNotifDialogOpen(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        disabled={testNotificationMutation.isPending || !testNotifOptions.fcm}
                        onClick={() => {
                            testNotificationMutation.mutate(
                                { id: user.id, ...testNotifOptions },
                                { onSuccess: () => setTestNotifDialogOpen(false) }
                            );
                        }}
                    >
                        Odeslat test
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Test Limit Dialog */}
            <Dialog open={resetLimitDialogOpen} onClose={() => setResetLimitDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Reset limitu testovacích poplachů</DialogTitle>
                <DialogContent>
                    <Typography>Resetovat limit testovacích poplachů pro uživatele <strong>{user.jmeno}</strong> na 5 za 24 hodin?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetLimitDialogOpen(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        disabled={resetTestLimitMutation.isPending}
                        onClick={() => {
                            resetTestLimitMutation.mutate(user.id, { onSuccess: () => setResetLimitDialogOpen(false) });
                        }}
                    >
                        Resetovat limit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Test History Dialog */}
            <Dialog open={testHistoryDialogOpen} onClose={() => setTestHistoryDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Historie testovacích poplachů — {user.jmeno}</DialogTitle>
                <DialogContent>
                    {testHistoryLoading ? (
                        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                    ) : testHistory && testHistory.length > 0 ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Stav</TableCell>
                                        <TableCell>Vytvořeno</TableCell>
                                        <TableCell>Naplánováno</TableCell>
                                        <TableCell>Provedeno</TableCell>
                                        <TableCell>Odpověď</TableCell>
                                        <TableCell>Čas odpovědi</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {testHistory.map((t: any) => (
                                        <TableRow key={t.id}>
                                            <TableCell>
                                                <Chip
                                                    label={t.stav}
                                                    size="small"
                                                    color={t.stav === 'DOKONCENO' ? 'success' : t.stav === 'ODESLANO' ? 'info' : t.stav === 'CEKAJICI' ? 'warning' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell>{t.createdAt ? dayjs(t.createdAt).format('DD.MM.YYYY HH:mm') : '—'}</TableCell>
                                            <TableCell>{t.scheduledTime ? dayjs(t.scheduledTime).format('DD.MM.YYYY HH:mm') : '—'}</TableCell>
                                            <TableCell>{t.executedAt ? dayjs(t.executedAt).format('DD.MM.YYYY HH:mm') : '—'}</TableCell>
                                            <TableCell>
                                                {t.odpoved && (
                                                    <Chip label={t.odpoved} size="small" color={t.odpoved === 'JEDU' ? 'success' : 'error'} />
                                                )}
                                            </TableCell>
                                            <TableCell>{t.odpovedCas ? dayjs(t.odpovedCas).format('DD.MM.YYYY HH:mm:ss') : '—'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Žádné testovací poplachy</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestHistoryDialogOpen(false)}>Zavřít</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Smazat uživatele?</DialogTitle>
                <DialogContent>
                    <Typography>Opravdu chcete smazat uživatele <strong>{user.jmeno}</strong>?</Typography>
                    <Typography sx={{ mt: 2 }}>
                        Účet se odstraní ze všech seznamů a přihlásit se s ním už nepůjde.
                        Zmizí e-mail, telefon, heslo i PIN. Záznamy o tom, ke kterým výjezdům
                        člen jel, zůstanou zachované včetně jména. Tuto akci nelze vrátit.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                            deleteUserMutation.mutate(user.id, { onSuccess: () => navigate('/admin/users') });
                        }}
                        disabled={deleteUserMutation.isPending}
                    >
                        Smazat
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DetailUserPage;
